<template>
  <div class="min-h-screen from-gray-900 to-gray-800 flex items-center justify-center px-4">
    <div class="w-full max-w-3xl">
      <!-- Title Area -->
      <div class="text-center mb-12">
        <h1 class="text-4xl font-bold text-white mb-4">Test Your Search</h1>
        <p class="text-gray-400 text-lg">Use this page to test your search configuration</p>
      </div>

      <!-- Search Bar -->
      <div class="relative">
        <input 
          type="text" 
          placeholder="Enter your search query..."
          class="w-full px-6 py-4 bg-white bg-opacity-10 rounded-xl text-white placeholder-gray-400 
                 border border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 
                 focus:outline-none transition-all duration-300
                 text-lg backdrop-blur-sm"
          v-model="searchQuery"
          @keyup.enter="() => handleSearch()"
          :disabled="isLoading"
        >
        <button 
          @click="() => handleSearch()"
          class="absolute right-3 top-1/2 transform -translate-y-1/2 
                 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg
                 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isLoading"
        >
          <div v-if="isLoading" class="animate-spin h-6 w-6 border-2 border-white border-t-transparent rounded-full"></div>
          <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
      </div>

      <!-- Metadata Section -->
      <div class="mt-4">
        <button 
          @click="showMetadata = !showMetadata"
          class="text-sm text-gray-400 hover:text-gray-300 transition-colors flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="showMetadata ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'" />
          </svg>
          {{ showMetadata ? 'Hide' : 'Add' }} Metadata
        </button>
        
        <div v-if="showMetadata" class="mt-3 bg-white bg-opacity-5 rounded-lg p-4 space-y-3">
          <div 
            v-for="(pair, index) in metadataPairs" 
            :key="index"
            class="flex items-center gap-2"
          >
            <input
              type="text"
              v-model="pair.key"
              placeholder="Key"
              class="flex-1 px-3 py-2 bg-white bg-opacity-10 rounded-lg text-white placeholder-gray-400 
                     border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
            />
            <input
              type="text"
              v-model="pair.value"
              placeholder="Value"
              class="flex-1 px-3 py-2 bg-white bg-opacity-10 rounded-lg text-white placeholder-gray-400 
                     border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
            />
            <button
              @click="removeMetadataPair(index)"
              class="p-2 text-gray-400 hover:text-red-400 transition-colors"
              title="Remove"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <button
            @click="addMetadataPair"
            class="text-sm text-blue-400 hover:text-blue-300 transition-colors flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Metadata Field
          </button>
        </div>
      </div>

      <!-- Response Times -->
      <div class="mt-4 flex justify-center items-center space-x-6">
        <div v-if="responseTime > 0" class="text-gray-400">
          Response time: {{ responseTime }}ms
        </div>
        <div v-if="searchHistory.length > 0" class="text-gray-400 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Avg. response time: {{ averageResponseTime }}ms
        </div>
      </div>

      <!-- Search History -->
      <div v-if="searchHistory.length > 0" class="mt-6">
        <h3 class="text-gray-400 text-sm mb-2">Recent Searches</h3>
        <div class="space-y-2">
          <div 
            v-for="(item, index) in searchHistory" 
            :key="index"
            class="flex items-center justify-between bg-white bg-opacity-5 rounded-lg p-3 hover:bg-opacity-10 transition-all group"
          >
            <div 
              class="flex flex-col flex-grow cursor-pointer"
              @click="handleSearch(item.query, item.metadata)"
            >
              <div class="flex items-center space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-gray-300">{{ item.query }}</span>
              </div>
              <div v-if="item.metadata && Object.keys(item.metadata).length > 0" class="mt-1 ml-7 flex flex-wrap gap-2">
                <span 
                  v-for="(value, key) in item.metadata" 
                  :key="key"
                  class="text-xs px-2 py-0.5 bg-blue-500 bg-opacity-20 text-blue-300 rounded"
                >
                  {{ key }}: {{ value }}
                </span>
              </div>
            </div>
            <div class="flex items-center space-x-4">
              <div class="text-sm text-gray-500">
                {{ item.responseTime }}ms
              </div>
              <button 
                @click.stop="viewSearchResult(item)"
                class="p-1 hover:bg-white hover:bg-opacity-10 rounded transition-colors"
                title="View result"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400 hover:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              <button 
                @click.stop="deleteSearchItem(index)"
                class="p-1 hover:bg-white hover:bg-opacity-10 rounded transition-colors"
                title="Remove from history"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400 hover:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Search Results -->
      <div v-if="searchResult" class="mt-12 bg-white bg-opacity-5 rounded-2xl p-6 backdrop-blur-sm">
        <!-- Dynamic Data Display -->
        <div v-for="(value, key) in searchResult.data" :key="key" class="mb-6">
          <div class="text-gray-400 text-sm mb-2 capitalize">{{ formatKey(String(key)) }}</div>
          
          <!-- Array Type -->
          <ul v-if="Array.isArray(value)" class="space-y-3">
            <li v-for="(item, index) in value" 
                :key="index"
                class="flex items-start text-gray-300">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-3 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              {{ item }}
            </li>
          </ul>

          <!-- Boolean Type -->
          <div v-else-if="typeof value === 'boolean'" class="flex items-center">
            <svg v-if="value" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span class="text-gray-300">{{ value ? 'Yes' : 'No' }}</span>
          </div>

          <!-- Number Type -->
          <div v-else-if="typeof value === 'number'" class="text-2xl font-bold text-white">
            {{ value }}
          </div>

          <!-- String Type (default) -->
          <div v-else class="text-2xl font-bold text-white">
            {{ value }}
          </div>
        </div>

        <!-- New Search Button -->
        <button 
          @click="resetSearch"
          class="mt-8 text-gray-400 hover:text-white transition-colors duration-300 flex items-center mx-auto"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
          Try another search
        </button>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mt-6 text-red-400 text-center">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useOrgStore } from '@/stores/org'

const searchQuery = ref('')
const isLoading = ref(false)
const error = ref('')
const searchResult = ref<any>(null)
const showMetadata = ref(false)
const metadataPairs = ref<Array<{key: string, value: string}>>([{key: '', value: ''}])

interface SearchHistoryItem {
  query: string;
  timestamp: Date;
  responseTime: number;
  result: any;
  metadata?: Record<string, string>;
}

const searchHistory = ref<Array<SearchHistoryItem>>([])
const responseTime = ref<number>(0)
const orgStore = useOrgStore()

const averageResponseTime = computed(() => {
  if (searchHistory.value.length === 0) return 0
  const total = searchHistory.value.reduce((sum, item) => sum + item.responseTime, 0)
  return Math.round(total / searchHistory.value.length)
})

const metadataObject = computed(() => {
  const obj: Record<string, string> = {}
  metadataPairs.value.forEach(pair => {
    if (pair.key.trim() && pair.value.trim()) {
      obj[pair.key.trim()] = pair.value.trim()
    }
  })
  return Object.keys(obj).length > 0 ? obj : null
})

const addMetadataPair = () => {
  metadataPairs.value.push({key: '', value: ''})
}

const removeMetadataPair = (index: number) => {
  if (metadataPairs.value.length > 1) {
    metadataPairs.value.splice(index, 1)
  } else {
    metadataPairs.value[0] = {key: '', value: ''}
  }
}

onMounted(async () => {
  await orgStore.fetchOrgData()
})

const handleSearch = async (queryOverride?: string, metadataOverride?: Record<string, string>) => {
  const queryToUse = queryOverride || searchQuery.value.trim()
  if (!queryToUse) return
  
  // Update the search input and metadata when using a history item
  if (queryOverride) {
    searchQuery.value = queryOverride
    if (metadataOverride) {
      // Restore metadata pairs from history
      metadataPairs.value = Object.entries(metadataOverride).map(([key, value]) => ({ key, value }))
      if (metadataPairs.value.length === 0) {
        metadataPairs.value = [{key: '', value: ''}]
      }
      showMetadata.value = true
    } else {
      // Clear metadata if none in history item
      metadataPairs.value = [{key: '', value: ''}]
    }
  }
  if (!orgStore.orgData?.id) {
    error.value = 'Please set up your organization first'
    return
  }
  
  isLoading.value = true
  error.value = ''
  searchResult.value = null
  responseTime.value = 0
  
  const startTime = performance.now()

  try {
    const isDev = process.env.NODE_ENV === 'development'
    console.log('[DEV] isDev', isDev) 
    const baseUrl = isDev 
      ? 'http://127.0.0.1:5001/search-plus-3bf60/australia-southeast1/queryV2' 
      : 'https://australia-southeast1-search-plus-3bf60.cloudfunctions.net/queryV2'

    const queryParams = new URLSearchParams({
      q: queryToUse,
      org_id: orgStore.orgData.id,
      test: 'true'
    })

    // Add metadata if provided - use override from history or current metadataObject
    const metadataToUse = metadataOverride || metadataObject.value
    if (metadataToUse) {
      queryParams.append('metadata', JSON.stringify(metadataToUse))
    }

    const response = await fetch(`${baseUrl}?${queryParams}`)
    const data = await response.json()
    
    const endTime = performance.now()
    responseTime.value = Math.round(endTime - startTime)
    
    if (data.success) {
      searchResult.value = data
      // Add to search history - use the metadata that was actually sent
      searchHistory.value.unshift({
        query: queryToUse,
        timestamp: new Date(),
        responseTime: responseTime.value,
        result: data,
        metadata: metadataToUse || undefined
      })
    } else {
      error.value = 'Sorry, we couldn\'t find any recommendations. Please try a different search.'
    }
  } catch (err) {
    error.value = 'Something went wrong. Please try again later.'
    console.error('Search error:', err)
  } finally {
    isLoading.value = false
  }
}

const getProductUrl = (productUrl: string) => {
  const storeName = orgStore.orgData?.keys?.shopify?.storeName
  if (!storeName) {
    return productUrl
  }
  // Remove any leading slash
  const cleanUrl = productUrl.replace(/^\//, '')
  return `https://${storeName}.myshopify.com/${cleanUrl}`
}

const viewSearchResult = (item: SearchHistoryItem) => {
  searchQuery.value = item.query
  searchResult.value = item.result
  responseTime.value = item.responseTime
  error.value = ''
  // Restore metadata if present
  if (item.metadata) {
    metadataPairs.value = Object.entries(item.metadata).map(([key, value]) => ({ key, value }))
    if (metadataPairs.value.length === 0) {
      metadataPairs.value = [{key: '', value: ''}]
    }
    showMetadata.value = true
  } else {
    metadataPairs.value = [{key: '', value: ''}]
  }
}

const deleteSearchItem = (index: number) => {
  searchHistory.value = searchHistory.value.filter((_, i) => i !== index)
}

const formatKey = (key: string): string => {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const resetSearch = () => {
  searchQuery.value = ''
  searchResult.value = null
  error.value = ''
  responseTime.value = 0
}
</script>

<style scoped>
input::placeholder {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

input:focus::placeholder {
  opacity: 0.4;
}
</style>
